import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const followUpSequenceArticle: BlogArticle = {
  key: BlogArticleKey.FOLLOW_UP_SEQUENCE,
  slug: BlogSlugs.FOLLOW_UP_SEQUENCE,
  categoryId: "05",
  date: "2026-08-30",
  readTime: 7,
  locales: {
    fr: {
      title: "Relancer sans harceler : la séquence qui obtient une réponse",
      excerpt:
        "La plupart des relances répètent le premier message en plus insistant. Une bonne séquence fait l'inverse : chaque message apporte quelque chose que le précédent n'avait pas.",
      description:
        "Combien de relances, à quel rythme, et quoi écrire dedans. Ce qui sépare une séquence qui obtient des réponses d'une qui agace.",
      ctaTitle: "Écris tes relances à partir de tes appels",
      ctaText:
        "On construit ta séquence par objectif et par canal, avec les mots que tes clients emploient réellement. Tu relis, tu corriges, tu envoies. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le premier message n'a pas eu de réponse. C'est le cas le plus fréquent, et de loin. Ce qui se passe ensuite décide de presque tout, et c'est là que la plupart des prospections se sabordent.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le réflexe est de renvoyer le même message, en plus court et en plus pressant. *Je me permets de revenir vers toi.* *Tu as pu regarder ?* *Je remonte ce message au cas où.* Trois relances de ce type et tu n'es plus quelqu'un qui propose quelque chose, tu es quelqu'un qui attend une réponse.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Une relance qui ne fait que rappeler la précédente demande à l'autre de faire l'effort. Une bonne relance apporte l'effort avec elle.",
        },
        { type: ArticleBlockType.H2, text: "Pourquoi on ne te répond pas" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Avant de décider du rythme, il faut savoir contre quoi tu joues. L'absence de réponse a presque toujours une de ces quatre causes, et aucune ne se règle en insistant.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Le message n'a pas été vu.** Le plus banal, et le seul cas où une simple relance suffit.",
            "**Il a été vu et n'a rien déclenché.** Répéter la même chose ne changera rien, il faut changer l'angle.",
            "**Le moment est mauvais.** La personne est concernée mais pas maintenant. Insister la brûle, patienter la garde.",
            "**Ce n'est pas la bonne personne.** Aucune séquence ne rattrapera un ciblage raté.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ces quatre cas appellent quatre suites différentes, et c'est précisément ce qu'une séquence automatique ne sait pas faire : elle envoie le message trois quoi qu'il arrive.",
        },
        { type: ArticleBlockType.H2, text: "Ce que chaque message doit apporter" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La règle tient en une phrase : si tu retires la mention de ton message précédent et qu'il ne reste rien, ce n'est pas une relance, c'est un rappel.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Le message", "Ce qu'il apporte de neuf"],
          rows: [
            [
              "Premier contact",
              "La situation que tu as repérée chez cette personne, et pourquoi elle t'a fait écrire",
            ],
            [
              "Deuxième message, quelques jours après",
              "Un angle différent sur le même problème, ou une conséquence que tu n'avais pas dite",
            ],
            [
              "Troisième message, une semaine après",
              "Quelque chose d'utile sans contrepartie : un cas, un chiffre, une ressource",
            ],
            [
              "Dernier message",
              "Une sortie propre, qui dit que tu arrêtes et laisse la porte ouverte",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le dernier message est celui qui obtient le plus de réponses, et c'est presque toujours celui qu'on n'envoie pas. Annoncer que tu arrêtes lève la pression, et une partie des gens répondent à ce moment-là précisément parce qu'ils ne se sentent plus poursuivis.",
        },
        { type: ArticleBlockType.H2, text: "Le rythme" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quatre messages sur trois semaines suffisent. Au-delà, tu n'ajoutes pas de chances, tu ajoutes de l'agacement. Et espace de plus en plus : deux ou trois jours entre les deux premiers, une semaine ensuite, dix jours pour le dernier.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cet espacement croissant n'est pas cosmétique. Il dit que tu n'es pas en train de dérouler une machine, et il laisse le temps à la situation de la personne de changer, ce qui est souvent la vraie raison d'une réponse tardive.",
        },
        { type: ArticleBlockType.H2, text: "Deux séquences côte à côte" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "La séquence qui agace",
            items: [
              "Chaque message rappelle le précédent",
              "Le même angle répété plus fort",
              "Un rythme régulier, visiblement automatique",
              "Une demande de rendez-vous à chaque fois",
              "Aucune fin annoncée, ça s'arrête sans rien dire",
            ],
          },
          good: {
            title: "La séquence qui obtient",
            items: [
              "Chaque message tient debout tout seul",
              "Un angle nouveau à chaque fois",
              "Un espacement qui s'allonge",
              "Une question à laquelle on répond en une ligne",
              "Une sortie annoncée, qui laisse la porte ouverte",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "Où trouver la matière" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le vrai problème d'une séquence n'est pas sa structure, c'est qu'il faut trois angles différents sur le même problème. C'est là que la page blanche arrive, et que les gens retombent sur le rappel poli.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ces angles existent déjà, dans tes appels. Les objections que tes clients ont formulées avant de signer sont des angles. Les conséquences qu'ils ont décrites sont des angles. Les mots qu'ils emploient pour nommer le problème valent mieux que les tiens, parce que ce sont ceux que la personne en face reconnaîtra.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Tes séquences se construisent à partir de ton avatar client, lui-même tiré de tes appels. Chaque message est proposé par objectif et par canal, avec les objections et les formulations réellement entendues. Tu relis, tu corriges, et c'est toi qui envoies.",
          linkLabel: "Découvrir les messages et séquences",
          linkPath: ProtectedPaths.MESSAGES_CONTENT,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quatre messages, des angles différents, un espacement qui s'allonge, et une sortie annoncée. Le test tient en une question : si tu retires la référence au message précédent, est-ce qu'il reste quelque chose. Si oui, c'est une relance. Sinon, c'est un rappel, et un rappel ne convainc personne.",
        },
      ],
      faq: [
        {
          question: "Combien de fois relancer un prospect sans réponse ?",
          answer:
            "Quatre messages sur environ trois semaines suffisent. Au-delà, tu n'augmentes pas tes chances, tu augmentes l'agacement. Ce qui change le résultat n'est pas le nombre mais le fait que chaque message apporte un angle que le précédent n'avait pas.",
        },
        {
          question: "Quel délai laisser entre deux relances ?",
          answer:
            "Un espacement croissant : deux à trois jours entre les deux premiers messages, une semaine ensuite, une dizaine de jours avant le dernier. Un rythme régulier se repère immédiatement comme automatique.",
        },
        {
          question: "Que mettre dans une relance ?",
          answer:
            "Quelque chose que le message précédent ne contenait pas : un autre angle sur le même problème, une conséquence non dite, ou une ressource utile sans contrepartie. Si en retirant la mention du message précédent il ne reste rien, ce n'est pas une relance.",
        },
        {
          question: "Faut-il annoncer qu'on arrête de relancer ?",
          answer:
            "Oui, et c'est souvent le message qui obtient le plus de réponses. Dire que tu arrêtes lève la pression, et une partie des gens répondent à ce moment précis parce qu'ils ne se sentent plus poursuivis. Laisse la porte ouverte sans conditions.",
        },
      ],
    },
    en: {
      title: "Following up without hounding: the sequence that gets a reply",
      excerpt:
        "Most follow-ups repeat the first message, more insistently. A good sequence does the opposite: every message brings something the previous one did not.",
      description:
        "How many follow-ups, at what pace, and above all what to put in them. What separates a sequence that gets replies from one that makes you a nuisance.",
      ctaTitle: "Write your follow-ups from your calls",
      ctaText:
        "We build your sequence by objective and by channel, in the words your customers actually use. You read it back, correct it, and send. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The first message got no reply. That is the most common outcome by far. What happens next decides almost everything, and it is where most outreach sinks itself.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The reflex is to resend the same message, shorter and more pressing. *Just circling back.* *Did you get a chance to look?* *Bumping this in case it got buried.* Three of those and you are no longer someone offering something, you are someone waiting for an answer.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A follow-up that only recalls the last one asks the other person to make the effort. A good follow-up brings the effort with it.",
        },
        { type: ArticleBlockType.H2, text: "Why you are not getting a reply" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Before deciding on a rhythm, you need to know what you are playing against. Silence almost always has one of four causes, and none of them is solved by insisting.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**The message was not seen.** The most ordinary case, and the only one a plain bump actually fixes.",
            "**It was seen and landed flat.** Repeating the same thing changes nothing; the angle has to change.",
            "**The timing is wrong.** The person is concerned, just not now. Pushing burns them, waiting keeps them.",
            "**It is the wrong person.** No sequence rescues bad targeting.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Those four cases call for four different continuations, which is exactly what an automated sequence cannot do: it sends message three regardless.",
        },
        { type: ArticleBlockType.H2, text: "What each message has to bring" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The rule fits in one sentence: if you remove the reference to your previous message and nothing is left, it is not a follow-up, it is a reminder.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["The message", "What it brings that is new"],
          rows: [
            [
              "First contact",
              "The situation you spotted about this person, and why it made you write",
            ],
            [
              "Second message, a few days later",
              "A different angle on the same problem, or a consequence you had not named",
            ],
            [
              "Third message, a week later",
              "Something useful with nothing asked in return: a case, a number, a resource",
            ],
            ["Last message", "A clean exit, saying you are stopping and leaving the door open"],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The last message is the one that gets the most replies, and almost always the one people never send. Announcing that you are stopping lifts the pressure, and some people answer at exactly that moment because they no longer feel chased.",
        },
        { type: ArticleBlockType.H2, text: "The pace" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Four messages over three weeks is enough. Beyond that you are not adding chances, you are adding irritation. And space them out further each time: two or three days between the first two, a week after that, ten days before the last.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That widening gap is not cosmetic. It says you are not running a machine, and it gives the person's situation time to change, which is often the real reason behind a late reply.",
        },
        { type: ArticleBlockType.H2, text: "Two sequences side by side" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The sequence that irritates",
            items: [
              "Every message recalls the previous one",
              "The same angle, repeated louder",
              "A steady rhythm, visibly automated",
              "A meeting request every single time",
              "No announced end, it just stops",
            ],
          },
          good: {
            title: "The sequence that works",
            items: [
              "Every message stands on its own",
              "A new angle each time",
              "A gap that widens",
              "A question answerable in one line",
              "An announced exit that leaves the door open",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "Where the material comes from" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The real difficulty of a sequence is not its structure, it is needing three different angles on the same problem. That is where the blank page arrives, and where people fall back on the polite reminder.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Those angles already exist, in your calls. The objections your customers raised before signing are angles. The consequences they described are angles. The words they use to name the problem beat yours, because they are the ones the person opposite will recognise.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "Your sequences are built from your client avatar, itself drawn from your calls. Each message is proposed by objective and by channel, using the objections and phrasings actually heard. You read it back, you correct it, and you are the one who sends.",
          linkLabel: "See how messages and sequences work",
          linkPath: ProtectedPaths.MESSAGES_CONTENT,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Four messages, different angles, a widening gap, and an announced exit. The test is one question: if you remove the reference to the previous message, is anything left. If yes, it is a follow-up. If not, it is a reminder, and a reminder convinces nobody.",
        },
      ],
      faq: [
        {
          question: "How many times should you follow up with an unresponsive prospect?",
          answer:
            "Four messages over about three weeks is enough. Beyond that you are not raising your chances, you are raising irritation. What changes the outcome is not the count but whether each message brings an angle the previous one lacked.",
        },
        {
          question: "How long should you wait between follow-ups?",
          answer:
            "A widening gap: two or three days between the first two messages, a week after that, around ten days before the last. A steady rhythm reads as automated immediately.",
        },
        {
          question: "What should a follow-up contain?",
          answer:
            "Something the previous message did not: another angle on the same problem, a consequence you had not named, or a useful resource with nothing asked in return. If removing the reference to the previous message leaves nothing, it is not a follow-up.",
        },
        {
          question: "Should you announce that you are stopping?",
          answer:
            "Yes, and it is often the message that gets the most replies. Saying you are stopping lifts the pressure, and some people answer at exactly that moment because they no longer feel chased. Leave the door open with no conditions.",
        },
      ],
    },
  },
};
